import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import AsyncSelect from "react-select/async";
import { useTranslate } from "../../../../components/TranslationText";
import EahsForm from "../../../../components/EahsForm";
import FormTextInput from "../../../../components/FormInputs/FormTextInput";
import FormDateInput from "../../../../components/FormInputs/FormDateInput";
import FormSelect from "../../../../components/FormInputs/FormSelect";
import { searchUnregisteredMaresApi } from "../../../../Redux/InternationalBreedingCertificate/internationalBreedingCertificateApi";
import { IBCFieldNames, cleanGuid } from "../utils/InternationalBreedingCertificateConstants";
import { TranslationKeys } from "../../../../constants/TranslationKeys";
import dayjs from "dayjs";

const fieldColClass = "col-md-6 col-sm-12 col-12 mb-2";

const IBCMareSection = ({ colors = [], countries = [], isReadOnly = false }) => {
    const { translate } = useTranslate();
    const { setValue } = useFormContext();

    const [searchMare, setSearchMare] = useState("");
    const [timeoutId, setTimeoutId] = useState(0);
    const [selectedMareOption, setSelectedMareOption] = useState(null);

    const promiseOptions = (searchKeyword) =>
        new Promise((resolve) => {
            if (timeoutId > 0) {
                clearTimeout(timeoutId);
            }

            const id = setTimeout(async () => {
                try {
                    const response = await searchUnregisteredMaresApi(searchKeyword);
                    const data = response.data?.result || [];
                    const options = data.map((item) => ({
                        label: `${item?.name || item?.nameEn || ''} (${item?.origin || ''})`,
                        value: item.id,
                        details: item,
                        ...item,
                    }));
                    resolve(options);
                } catch (error) {
                    console.error("Error searching unregistered mares:", error);
                    resolve([]);
                }
            }, 600);

            setTimeoutId(id);
        });

    const handleSelectMare = (option) => {
        setSelectedMareOption(option);
        if (option?.details) {
            const m = option.details;
            setValue(IBCFieldNames.UNREGISTERED_MARE_ID, cleanGuid(m.id) || null, { shouldValidate: true });
            setValue(IBCFieldNames.MARE_NAME_EN, m.nameEn || m.name || '', { shouldValidate: true });
            setValue(IBCFieldNames.MARE_NAME_AR, m.nameAr || m.name || '', { shouldValidate: true });
            setValue(IBCFieldNames.MARE_SIRE_NAME_EN, m.sireEn || m.sire || '', { shouldValidate: true });
            setValue(IBCFieldNames.MARE_SIRE_NAME_AR, m.sireAr || m.sire || '', { shouldValidate: true });
            setValue(IBCFieldNames.MARE_DAM_NAME_EN, m.damEn || m.dam || '', { shouldValidate: true });
            setValue(IBCFieldNames.MARE_DAM_NAME_AR, m.damAr || m.dam || '', { shouldValidate: true });
            setValue(IBCFieldNames.RECORDED_MARE_OWNER_NAME_EN, m.ownerEn || m.owner || m.ownerName || '', { shouldValidate: true });
            setValue(IBCFieldNames.RECORDED_MARE_OWNER_NAME_AR, m.ownerAr || m.owner || m.ownerName || '', { shouldValidate: true });
            setValue(IBCFieldNames.MARE_BREEDER_NAME_EN, m.breederEn || m.breeder || '', { shouldValidate: true });
            setValue(IBCFieldNames.MARE_BREEDER_NAME_AR, m.breederAr || m.breeder || '', { shouldValidate: true });
            setValue(IBCFieldNames.MARE_HORSE_ADDRESS, m.address || m.horseAddress || m.mareHorseAddress || '', { shouldValidate: true });
            setValue(IBCFieldNames.MARE_REGISTRATION_NUMBER, m.registerNumber || m.regNo || '', { shouldValidate: true });
            setValue(IBCFieldNames.MARE_UELN, m.horseUeln || m.ueln || '', { shouldValidate: true });

            if (m.birthDate) {
                const dmyMatch = String(m.birthDate).trim().match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
                if (dmyMatch) {
                    const day = dmyMatch[1].padStart(2, '0');
                    const month = dmyMatch[2].padStart(2, '0');
                    const year = dmyMatch[3];
                    setValue(IBCFieldNames.MARE_BIRTH_DATE, `${year}-${month}-${day}`, { shouldValidate: true });
                } else {
                    setValue(IBCFieldNames.MARE_BIRTH_DATE, m.birthDate.split('T')[0], { shouldValidate: true });
                }
            }

            // Origin Country match
            let originCountry = cleanGuid(m.originId || m.mareOriginCountryId || m.mareOriginId);
            if (!originCountry && m.origin && countries?.length > 0) {
                const originText = String(m.origin).trim().toLowerCase();
                const matched = countries.find(
                    (c) =>
                        String(c.text || c.label || c.name || '').trim().toLowerCase() === originText ||
                        String(c.code || '').trim().toLowerCase() === originText
                );
                if (matched) {
                    originCountry = cleanGuid(matched.value || matched.id);
                }
            }
            setValue(IBCFieldNames.MARE_ORIGIN_COUNTRY_ID, originCountry || null, { shouldValidate: true });

            // Studbook Country match
            let currentCountry = cleanGuid(m.currentCountryId || m.mareStudbookCountryId || m.mareHorseCountryId);
            if (!currentCountry && m.currentCountry && countries?.length > 0) {
                const countryText = String(m.currentCountry).trim().toLowerCase();
                const matched = countries.find(
                    (c) =>
                        String(c.text || c.label || c.name || '').trim().toLowerCase() === countryText ||
                        String(c.code || '').trim().toLowerCase() === countryText
                );
                if (matched) {
                    currentCountry = cleanGuid(matched.value || matched.id);
                }
            }
            setValue(IBCFieldNames.MARE_STUDBOOK_COUNTRY_ID, currentCountry || originCountry || null, { shouldValidate: true });

            // Color match
            let horseColor = cleanGuid(m.colorId || m.mareColorId);
            if (!horseColor && m.color && colors?.length > 0) {
                const colorText = String(m.color).trim().toLowerCase();
                const matched = colors.find(
                    (c) => String(c.text || c.label || c.name || '').trim().toLowerCase() === colorText
                );
                if (matched) {
                    horseColor = cleanGuid(matched.value || matched.id);
                }
            }
            setValue(IBCFieldNames.COLOR_ID, horseColor || null, { shouldValidate: true });
        } else {
            setValue(IBCFieldNames.UNREGISTERED_MARE_ID, null, { shouldValidate: true });
        }
    };

    return (
        <EahsForm
            isSectionTitle
            className={'mb-main'}
            ish3
            title={translate(TranslationKeys.MARE_DETAILS, 'Mare Details')}
        >
            <div className="row">
                {!isReadOnly && (
                    <div className="col-12 mb-3">
                        <label className="capatalize_text mb-1">
                            {translate(TranslationKeys.FIND_EXISTING_MARE, 'Find Existing Mare')}
                        </label>
                        <AsyncSelect
                            isClearable
                            loadOptions={promiseOptions}
                            defaultOptions={false}
                            onInputChange={(v) => setSearchMare(v)}
                            inputValue={searchMare}
                            value={selectedMareOption}
                            loadingMessage={() => translate(TranslationKeys.LOADING, 'Loading')}
                            onChange={handleSelectMare}
                            placeholder={translate(TranslationKeys.TYPE_TO_SEARCH, 'Type To Search') + '...'}
                            noOptionsMessage={() => translate(TranslationKeys.NO_OPTIONS, 'No Options')}
                            isDisabled={isReadOnly}
                        />
                    </div>
                )}

                <div className={fieldColClass}>
                    <FormTextInput
                        name={IBCFieldNames.MARE_NAME_EN}
                        label={`${translate(TranslationKeys.NAME, 'Name')} (EN)`}
                        required
                        disabled={isReadOnly}
                    />
                </div>
                <div className={fieldColClass}>
                    <FormTextInput
                        name={IBCFieldNames.MARE_NAME_AR}
                        label={`${translate(TranslationKeys.NAME, 'Name')} (AR)`}
                        required
                        disabled={isReadOnly}
                    />
                </div>

                <div className={fieldColClass}>
                    <FormTextInput
                        name={IBCFieldNames.MARE_SIRE_NAME_EN}
                        label={`${translate(TranslationKeys.SIRE, 'Sire')} (EN)`}
                        required
                        disabled={isReadOnly}
                    />
                </div>
                <div className={fieldColClass}>
                    <FormTextInput
                        name={IBCFieldNames.MARE_SIRE_NAME_AR}
                        label={`${translate(TranslationKeys.SIRE, 'Sire')} (AR)`}
                        required
                        disabled={isReadOnly}
                    />
                </div>

                <div className={fieldColClass}>
                    <FormTextInput
                        name={IBCFieldNames.MARE_DAM_NAME_EN}
                        label={`${translate(TranslationKeys.DAM, 'Dam')} (EN)`}
                        required
                        disabled={isReadOnly}
                    />
                </div>
                <div className={fieldColClass}>
                    <FormTextInput
                        name={IBCFieldNames.MARE_DAM_NAME_AR}
                        label={`${translate(TranslationKeys.DAM, 'Dam')} (AR)`}
                        required
                        disabled={isReadOnly}
                    />
                </div>

                <div className={fieldColClass}>
                    <FormTextInput
                        name={IBCFieldNames.RECORDED_MARE_OWNER_NAME_EN}
                        label={`${translate(TranslationKeys.OWNER_NAME, 'Owner Name')} (EN)`}
                        required
                        disabled={isReadOnly}
                    />
                </div>
                <div className={fieldColClass}>
                    <FormTextInput
                        name={IBCFieldNames.RECORDED_MARE_OWNER_NAME_AR}
                        label={`${translate(TranslationKeys.OWNER_NAME, 'Owner Name')} (AR)`}
                        required
                        disabled={isReadOnly}
                    />
                </div>

                <div className={fieldColClass}>
                    <FormTextInput
                        name={IBCFieldNames.MARE_HORSE_ADDRESS}
                        label={translate(TranslationKeys.HORSE_ADDRESS, 'Horse Address')}
                        required
                        disabled={isReadOnly}
                    />
                </div>

                <div className={fieldColClass}>
                    <FormTextInput
                        name={IBCFieldNames.MARE_BREEDER_NAME_EN}
                        label={`${translate(TranslationKeys.BREEDER, 'Breeder Name')} (EN)`}
                        required
                        disabled={isReadOnly}
                    />
                </div>
                <div className={fieldColClass}>
                    <FormTextInput
                        name={IBCFieldNames.MARE_BREEDER_NAME_AR}
                        label={`${translate(TranslationKeys.BREEDER, 'Breeder Name')} (AR)`}
                        required
                        disabled={isReadOnly}
                    />
                </div>

                <div className={fieldColClass}>
                    <FormTextInput
                        name={IBCFieldNames.MARE_REGISTRATION_NUMBER}
                        label={translate(TranslationKeys.REG_NO, 'Reg No')}
                        required
                        disabled={isReadOnly}
                    />
                </div>
                <div className={fieldColClass}>
                    <FormTextInput
                        name={IBCFieldNames.MARE_UELN}
                        label={translate(TranslationKeys.UELN, 'UELN')}
                        disabled={isReadOnly}
                    />
                </div>

                <div className={fieldColClass}>
                    <FormSelect
                        name={IBCFieldNames.COLOR_ID}
                        label={translate(TranslationKeys.COLOR, 'Color')}
                        options={[{ label: translate(TranslationKeys.COLOR, 'Color'), value: "" }, ...colors]}
                        required
                        disabled={isReadOnly}
                    />
                </div>
                <div className={fieldColClass}>
                    <FormSelect
                        name={IBCFieldNames.MARE_ORIGIN_COUNTRY_ID}
                        label={translate(TranslationKeys.ORIGIN, 'Origin')}
                        options={[{ label: translate(TranslationKeys.ORIGIN, 'Origin'), value: "" }, ...countries]}
                        required
                        disabled={isReadOnly}
                    />
                </div>

                <div className={fieldColClass}>
                    <FormSelect
                        name={IBCFieldNames.MARE_STUDBOOK_COUNTRY_ID}
                        label={translate(TranslationKeys.HORSE_COUNTRY, 'Horse Country')}
                        options={[{ label: translate(TranslationKeys.HORSE_COUNTRY, 'Horse Country'), value: "" }, ...countries]}
                        required
                        disabled={isReadOnly}
                    />
                </div>
                <div className={fieldColClass}>
                    <FormDateInput
                        name={IBCFieldNames.MARE_BIRTH_DATE}
                        label={translate(TranslationKeys.BIRTH_DATE, 'Birth Date')}
                        required
                        disabled={isReadOnly}
                        maxDate={dayjs().format("YYYY-MM-DD")}
                    />
                </div>
            </div>
        </EahsForm>
    );
};

export default IBCMareSection;
